// (c) 2026 Planet Q Productions. All Rights Reserved.

import React, { useRef, useEffect } from 'react';

const FlightMode = ({ 
  backgroundImage,
  characterImage, 
  enemyImage,
  gameTitle,
  gameSpeed,
  storyImages,
  dynamicStories,
  isPlaying,
  onScoreUpdate,
  onGameOver,
  onStageComplete,
}) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const imagesRef = useRef({ bg: null, ch: null, en: null });
  const storyImgRef = useRef({});

  // Store callbacks in refs to prevent game loop restart
  const onScoreRef = useRef(onScoreUpdate);
  const onGameOverRef = useRef(onGameOver);
  const onStageRef = useRef(onStageComplete);
  const dynamicStoriesRef = useRef(dynamicStories);
  useEffect(() => { onScoreRef.current = onScoreUpdate; }, [onScoreUpdate]);
  useEffect(() => { onGameOverRef.current = onGameOver; }, [onGameOver]);
  useEffect(() => { onStageRef.current = onStageComplete; }, [onStageComplete]);
  useEffect(() => { dynamicStoriesRef.current = dynamicStories; }, [dynamicStories]);

  // Load story images as they become available
  useEffect(() => {
    if (!storyImages) return;
    Object.entries(storyImages).forEach(([idx, src]) => {
      if (storyImgRef.current[idx]) return;
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => { storyImgRef.current[idx] = img; };
      img.src = src;
    });
  }, [storyImages]);

  useEffect(() => {
    const load = (src, key) => {
      if (!src) return;
      const img = new Image();
      if (src.startsWith('http')) img.crossOrigin = 'anonymous';
      img.onload = () => { imagesRef.current[key] = img; };
      img.onerror = () => { console.warn(`FlightMode: failed to load ${key}`); };
      img.src = src;
    };
    load(backgroundImage, 'bg');
    load(characterImage, 'ch');
    load(enemyImage, 'en');
  }, [backgroundImage, characterImage, enemyImage]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const resize = () => {
      const w = canvas.parentElement?.offsetWidth || window.innerWidth;
      const h = canvas.parentElement?.offsetHeight || window.innerHeight;
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w; canvas.height = h;
      }
      return { w, h };
    };

    // Forward zoom constants
    const ZOOM_RANGE = 0.2;
    const VP_X = 0.5;
    const VP_Y = 0.4;

    const s = {
      frame: 0,
      playerX: 0.5, playerY: 0.7,
      speed: 2, score: 0,
      roll: 0, targetRoll: 0,
      enemies: [], enemyTimer: 0,
      bullets: [],
      explosions: [],
      gameOver: false, lives: 3, invincible: 0,
      zoomPhase: 0,
      deathExplosion: null,
      stage: 1, stageScore: 0, stageThreshold: 300,
      celebrating: false, celebTimer: 0,
      storyPhase: false, storyTimer: 0, storyWaitingForInput: false,
      introPhase: true, introTimer: 0, introWaitingForInput: false,
      endingPhase: false, endingTimer: 0,
      celebParticles: [],
      maxStages: 10,
    };

    // Fallback stories for Neon Drift
    const neonDriftStories = [
      { title: 'NEON ALLEY', desc: 'The android ducks into a flickering neon alleyway...', scene: 'Rain drips off holographic signs. A moment to recharge.', colors: ['#1a0a3d','#2d1a5a','#0d0520'] },
      { title: 'RAMEN HIDEOUT', desc: 'A hidden ramen bar behind a glitching billboard...', scene: 'Steam rises from cyber-ramen. The circuits cool down.', colors: ['#3d1a0a','#5a2d1a','#200d05'] },
      { title: 'ROOFTOP HAVEN', desc: 'Scaling a tower to the rain-soaked rooftop...', scene: 'The city glows below. Police drones patrol the distance.', colors: ['#0a1a3d','#1a2d5a','#050d20'] },
      { title: 'HACKER DEN', desc: 'Slipping through a hidden door into the underground...', scene: 'Screens flicker with code. Allies share intel on drone routes.', colors: ['#0a2d1a','#1a4a2d','#05200d'] },
      { title: 'METRO STATION', desc: 'An abandoned metro station offers shelter...', scene: 'Old trains rust in the dark. But the power cells still work.', colors: ['#1a1a2d','#2d2d4a','#0d0d1a'] },
      { title: 'ARCADE RUINS', desc: 'A destroyed arcade still has one machine running...', scene: 'Pixel art glows in the dark. One last game before the next run.', colors: ['#2d0a2d','#4a1a4a','#1a051a'] },
      { title: 'DRONE FACTORY', desc: 'Infiltrating the drone factory from below...', scene: 'Assembly lines build enemies. But there is a weakness here.', colors: ['#1a2a1a','#2d402d','#0d1a0d'] },
      { title: 'SKY BRIDGE', desc: 'Crossing a bridge high above the neon streets...', scene: 'Wind howls between towers. The rain is horizontal up here.', colors: ['#0a1a2d','#1a3050','#050d1a'] },
      { title: 'CIRCUIT TEMPLE', desc: 'An ancient server room, still humming with power...', scene: 'Data flows like rivers of light. An upgrade awaits.', colors: ['#2d1a0a','#4a2d1a','#1a0d05'] },
      { title: 'COMMAND SPIRE', desc: 'The tallest tower in the district. The final refuge.', scene: 'The whole city visible below. The android is ready for anything.', colors: ['#1a0a2d','#3d1a5a','#0d0520'] },
    ];
    const isNeonDrift = (gameTitle || '').toLowerCase().includes('neon drift');

    const keys = {};
    const onKeyDown = (e) => {
      keys[e.key] = true;
      if (e.key === 'Enter' || e.key === ' ') {
        if (s.introWaitingForInput) { s.introPhase = false; s.introTimer = 0; s.introWaitingForInput = false; return; }
        if (s.storyWaitingForInput) { s.storyPhase = false; s.storyTimer = 0; s.storyWaitingForInput = false; return; }
        if (s.endingPhase) { s.endingPhase = false; if (onGameOverRef.current) onGameOverRef.current(Math.floor(s.score), true); return; }
      }
    };
    const onKeyUp = (e) => { keys[e.key] = false; };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);

    const advanceStory = () => {
      if (s.introWaitingForInput) { s.introPhase = false; s.introTimer = 0; s.introWaitingForInput = false; }
      else if (s.storyWaitingForInput) { s.storyPhase = false; s.storyTimer = 0; s.storyWaitingForInput = false; }
      else if (s.endingPhase) { s.endingPhase = false; if (onGameOverRef.current) onGameOverRef.current(Math.floor(s.score), true); }
    };

    window.gameActions = {
      moveUp: () => { s.playerY = Math.max(0.15, s.playerY - 0.08); },
      moveDown: () => { s.playerY = Math.min(0.9, s.playerY + 0.08); },
      moveLeft: () => { s.playerX = Math.max(0.05, s.playerX - 0.06); s.targetRoll = -0.3; },
      moveRight: () => { s.playerX = Math.min(0.95, s.playerX + 0.06); s.targetRoll = 0.3; },
      shoot: () => {
        if (!s.gameOver) s.bullets.push({ x: s.playerX, y: s.playerY, z: 0, speed: 0.04 });
      },
      advanceStory,
    };

    // Draw environment zoomed (full-screen forward motion)
    const drawEnvZoomed = (img, w, h, zoom) => {
      const imgR = img.width / img.height;
      const canR = w / h;
      let sx = 0, sy = 0, sw = img.width, sh = img.height;
      if (imgR > canR) { sw = img.height * canR; sx = (img.width - sw) / 2; }
      else { sh = img.width / canR; sy = (img.height - sh) / 2; }
      const dw = w * zoom;
      const dh = h * zoom;
      const dx = -(dw - w) * VP_X;
      const dy = -(dh - h) * VP_Y;
      ctx.drawImage(img, sx, sy, sw, sh, dx, dy, dw, dh);
    };

    // Spawn explosion particles
    const spawnExplosion = (x, y, z, count, big) => {
      const colors = ['#FF4400', '#FF8800', '#FFCC00', '#FFFFFF', '#FF2200'];
      for (let i = 0; i < count; i++) {
        const angle = Math.random() * Math.PI * 2;
        const spd = (big ? 3 : 1.5) + Math.random() * (big ? 5 : 3);
        s.explosions.push({
          x, y, z,
          vx: Math.cos(angle) * spd * 0.005,
          vy: Math.sin(angle) * spd * 0.005,
          life: 25 + Math.random() * 20,
          maxLife: 45,
          size: big ? 8 + Math.random() * 12 : 4 + Math.random() * 6,
          color: colors[Math.floor(Math.random() * colors.length)],
        });
      }
    };

    let running = true;

    const loop = () => {
      if (!running) return;

      // Check pause
      if (window.gamePaused) {
        animationRef.current = requestAnimationFrame(loop);
        return;
      }

      s.frame++;
      const { w, h } = resize();
      const images = imagesRef.current;
      const vanishX = w * 0.5;
      const vanishY = h * 0.4;

      ctx.clearRect(0, 0, w, h);

      // ──── INTRO STORY (before gameplay starts) ────
      if (s.introPhase) {
        s.introTimer++;
        const t = s.introTimer;
        if (images.bg) drawEnvZoomed(images.bg, w, h, 1.0);
        else { ctx.fillStyle = '#050520'; ctx.fillRect(0, 0, w, h); }
        ctx.fillStyle = 'rgba(0,0,0,0.7)'; ctx.fillRect(0, 0, w, h);

        let introStory;
        if (dynamicStoriesRef.current && dynamicStoriesRef.current[0]) {
          const ds = dynamicStoriesRef.current[0];
          introStory = { title: (gameTitle || 'TAKE FLIGHT').toUpperCase(), desc: ds.desc || 'Prepare for takeoff...', scene: ds.scene || 'The skies await.' };
        } else {
          introStory = { title: (gameTitle || 'TAKE FLIGHT').toUpperCase(), desc: 'Your mission begins now...', scene: 'Enemy forces are incoming. Take to the skies and fight!' };
        }

        const titleAlpha = Math.min(1, t / 30);
        ctx.globalAlpha = titleAlpha;
        ctx.fillStyle = '#FFD700';
        ctx.font = `bold ${Math.min(w * 0.07, 48)}px Rajdhani, sans-serif`;
        ctx.textAlign = 'center';
        ctx.fillText(introStory.title, w / 2, h * 0.22);
        const lineW = Math.min(w * 0.5, 300) * Math.min(1, t / 40);
        ctx.strokeStyle = 'rgba(255,215,0,0.5)'; ctx.lineWidth = 1;
        ctx.beginPath(); ctx.moveTo(w / 2 - lineW / 2, h * 0.26); ctx.lineTo(w / 2 + lineW / 2, h * 0.26); ctx.stroke();

        if (t > 20) { ctx.globalAlpha = Math.min(1, (t - 20) / 20); ctx.fillStyle = '#ddd'; ctx.font = `${Math.min(w * 0.035, 22)}px Rajdhani, sans-serif`; ctx.fillText(introStory.desc, w / 2, h * 0.34); }
        if (t > 40) {
          const visChars = Math.min(introStory.scene.length, Math.floor((t - 40) * 1.2));
          ctx.globalAlpha = Math.min(1, (t - 40) / 15); ctx.fillStyle = 'rgba(255,255,255,0.7)'; ctx.font = `italic ${Math.min(w * 0.03, 18)}px Rajdhani, sans-serif`;
          ctx.fillText(introStory.scene.substring(0, visChars), w / 2, h * 0.42);
        }
        if (t > 30 && images.ch) { ctx.globalAlpha = Math.min(1, (t - 30) / 20); const cs = Math.min(w * 0.2, 140); ctx.drawImage(images.ch, w / 2 - cs / 2, h * 0.48 + Math.sin(t * 0.04) * 5, cs, cs * 1.2); }

        if (t > 70) {
          s.introWaitingForInput = true;
          const flash = Math.sin(t * 0.06) * 0.3 + 0.7;
          ctx.globalAlpha = flash; ctx.fillStyle = '#00F0FF';
          ctx.font = `bold ${Math.min(w * 0.04, 24)}px Rajdhani, sans-serif`;
          ctx.fillText('PRESS START TO BEGIN', w / 2, h * 0.82);
          ctx.globalAlpha = flash * 0.15; const btnW = Math.min(w * 0.45, 280);
          ctx.fillStyle = '#00F0FF'; ctx.beginPath(); ctx.roundRect(w / 2 - btnW / 2, h * 0.78, btnW, 30, 15); ctx.fill();
        }

        ctx.globalAlpha = 0.6; ctx.fillStyle = 'rgba(0,0,0,0.5)'; ctx.fillRect(0, 0, w, 32);
        ctx.fillStyle = '#FFD700'; ctx.font = 'bold 13px Rajdhani, sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('PROLOGUE', w / 2, 20);
        ctx.globalAlpha = 1;
        animationRef.current = requestAnimationFrame(loop);
        return;
      }

      // ──── ENDING STORY ────
      if (s.endingPhase) {
        s.endingTimer++;
        const t = s.endingTimer;
        if (images.bg) drawEnvZoomed(images.bg, w, h, 1.0);
        else { ctx.fillStyle = '#050520'; ctx.fillRect(0, 0, w, h); }
        ctx.fillStyle = 'rgba(0,0,0,0.75)'; ctx.fillRect(0, 0, w, h);

        if (t % 4 === 0) {
          for (let i = 0; i < 4; i++)
            s.celebParticles.push({ x: Math.random() * w, y: h + 10, vx: (Math.random() - 0.5) * 4, vy: -Math.random() * 8 - 3, life: 50, color: ['#FFD700','#FF69B4','#00FF00','#00BFFF','#FF4400'][i % 5] });
        }
        s.celebParticles = s.celebParticles.filter(p => { p.x += p.vx; p.y += p.vy; p.vy += 0.1; p.life--; return p.life > 0; });
        s.celebParticles.forEach(p => { ctx.globalAlpha = p.life / 50; ctx.fillStyle = p.color; ctx.beginPath(); ctx.arc(p.x, p.y, 4, 0, Math.PI * 2); ctx.fill(); });
        ctx.globalAlpha = 1;

        ctx.globalAlpha = Math.min(1, t / 30); ctx.fillStyle = '#FFD700';
        ctx.font = `bold ${Math.min(w * 0.08, 56)}px Rajdhani, sans-serif`; ctx.textAlign = 'center';
        ctx.fillText('VICTORY!', w / 2, h * 0.2);
        ctx.fillStyle = '#fff'; ctx.font = `${Math.min(w * 0.04, 26)}px Rajdhani, sans-serif`;
        ctx.fillText(`${gameTitle || 'PLANET Q'} — COMPLETE`, w / 2, h * 0.28);

        if (t > 20) { ctx.globalAlpha = Math.min(1, (t - 20) / 20); ctx.fillStyle = '#00F0FF'; ctx.font = `bold ${Math.min(w * 0.06, 40)}px Rajdhani, sans-serif`; ctx.fillText(`FINAL SCORE: ${Math.floor(s.score)}`, w / 2, h * 0.4); }
        if (t > 40) {
          let endText = 'The skies are clear. You are the ultimate ace!';
          if (dynamicStoriesRef.current && dynamicStoriesRef.current[9]) endText = dynamicStoriesRef.current[9].scene || endText;
          const visChars = Math.min(endText.length, Math.floor((t - 40) * 1.0));
          ctx.globalAlpha = Math.min(1, (t - 40) / 15); ctx.fillStyle = 'rgba(255,255,255,0.8)';
          ctx.font = `italic ${Math.min(w * 0.03, 20)}px Rajdhani, sans-serif`; ctx.fillText(endText.substring(0, visChars), w / 2, h * 0.52);
        }
        if (t > 30 && images.ch) { ctx.globalAlpha = Math.min(1, (t - 30) / 20); const cs = Math.min(w * 0.2, 140); ctx.drawImage(images.ch, w / 2 - cs / 2, h * 0.58 + Math.sin(t * 0.06) * 8, cs, cs * 1.2); }
        if (t > 80) { const flash = Math.sin(t * 0.06) * 0.3 + 0.7; ctx.globalAlpha = flash; ctx.fillStyle = '#FFD700'; ctx.font = `bold ${Math.min(w * 0.035, 22)}px Rajdhani, sans-serif`; ctx.fillText('PRESS START TO FINISH', w / 2, h * 0.88); }
        ctx.globalAlpha = 1;
        animationRef.current = requestAnimationFrame(loop);
        return;
      }

      // ──── GAME OVER ────
      if (s.gameOver) {
        // Draw final frame with death explosion
        if (images.bg) {
          drawEnvZoomed(images.bg, w, h, 1.0);
        } else {
          ctx.fillStyle = '#050520'; ctx.fillRect(0, 0, w, h);
        }
        ctx.fillStyle = 'rgba(0,0,0,0.6)'; ctx.fillRect(0, 0, w, h);

        // Draw remaining explosion particles
        if (s.deathExplosion && s.deathExplosion.life > 0) {
          s.deathExplosion.life--;
          s.explosions.forEach(ex => {
            ex.x += ex.vx; ex.y += ex.vy; ex.life--;
          });
          s.explosions = s.explosions.filter(ex => ex.life > 0);
          s.explosions.forEach(ex => {
            const sx = ex.x * w;
            const sy = ex.y * h;
            ctx.globalAlpha = ex.life / ex.maxLife;
            ctx.fillStyle = ex.color;
            ctx.beginPath(); ctx.arc(sx, sy, ex.size, 0, Math.PI * 2); ctx.fill();
          });
          ctx.globalAlpha = 1;
          animationRef.current = requestAnimationFrame(loop);
          return;
        }

        if (onGameOverRef.current) onGameOverRef.current(Math.floor(s.score));
        return;
      }

      // ──── INPUT (skip during celebration/story/intro/ending) ────
      if (!s.celebrating && !s.storyPhase && !s.introPhase && !s.endingPhase) {
      if (keys['ArrowUp'] || keys['w']) s.playerY = Math.max(0.1, s.playerY - 0.02);
      if (keys['ArrowDown'] || keys['s']) s.playerY = Math.min(0.92, s.playerY + 0.02);
      if (keys['ArrowLeft'] || keys['a']) { s.playerX = Math.max(0.05, s.playerX - 0.02); s.targetRoll = -0.3; }
      else if (keys['ArrowRight'] || keys['d']) { s.playerX = Math.min(0.95, s.playerX + 0.02); s.targetRoll = 0.3; }
      else { s.targetRoll = 0; }
      if (keys[' '] || keys['f']) {
        if (s.frame % 8 === 0) s.bullets.push({ x: s.playerX, y: s.playerY, z: 0, speed: 0.04 });
      }
      }

      s.roll += (s.targetRoll - s.roll) * 0.1;
      s.score += 0.2;
      s.stageScore += 0.2;
      if (s.invincible > 0) s.invincible--;
      s.zoomPhase = (s.zoomPhase + 0.0015 * s.speed) % 1.0;
      if (onScoreRef.current) onScoreRef.current(Math.floor(s.score));

      // ──── STAGE COMPLETE CHECK ────
      if (s.stageScore >= s.stageThreshold && !s.celebrating && !s.storyPhase) {
        s.celebrating = true;
        s.celebTimer = 0;
        s.stage++;
        s.stageScore = 0;
        s.stageThreshold = Math.min(600, s.stageThreshold + 30);
        s.enemies = [];
        s.bullets = [];
      }

      // ──── CELEBRATING ────
      if (s.celebrating) {
        s.celebTimer++;
        if (images.bg) drawEnvZoomed(images.bg, w, h, 1.0 + ZOOM_RANGE * 0.5);
        else { ctx.fillStyle = '#050520'; ctx.fillRect(0, 0, w, h); }
        ctx.fillStyle = 'rgba(0,0,0,0.55)'; ctx.fillRect(0, 0, w, h);
        ctx.fillStyle = '#FFD700';
        ctx.font = `bold ${Math.min(w * 0.06, 42)}px Rajdhani, sans-serif`;
        ctx.textAlign = 'center';
        ctx.fillText(`STAGE ${s.stage - 1} COMPLETE!`, w / 2, h * 0.35);
        ctx.fillStyle = '#fff';
        ctx.font = `${Math.min(w * 0.04, 26)}px Rajdhani, sans-serif`;
        ctx.fillText(`Score: ${Math.floor(s.score)}`, w / 2, h * 0.43);
        if (s.frame % 3 === 0) {
          for (let i = 0; i < 3; i++)
            s.celebParticles.push({ x: Math.random() * w, y: h * 0.5, vx: (Math.random() - 0.5) * 7, vy: -Math.random() * 7 - 2, life: 35, color: ['#FFD700','#FF69B4','#00FF00','#00BFFF'][i % 4] });
        }
        s.celebParticles = s.celebParticles.filter(p => { p.x += p.vx; p.y += p.vy; p.vy += 0.15; p.life--; return p.life > 0; });
        s.celebParticles.forEach(p => { ctx.globalAlpha = p.life / 35; ctx.fillStyle = p.color; ctx.beginPath(); ctx.arc(p.x, p.y, 4, 0, Math.PI * 2); ctx.fill(); });
        ctx.globalAlpha = 1;
        if (s.celebTimer > 100) {
          s.celebrating = false; s.celebTimer = 0; s.celebParticles = [];
          if (s.stage > s.maxStages) {
            s.endingPhase = true; s.endingTimer = 0;
            animationRef.current = requestAnimationFrame(loop);
            return;
          }
          s.storyPhase = true; s.storyTimer = 0; s.storyWaitingForInput = false;
          if (onStageRef.current) onStageRef.current(s.stage);
        }
        animationRef.current = requestAnimationFrame(loop);
        return;
      }

      // ──── STORY CUTSCENE ────
      if (s.storyPhase) {
        s.storyTimer++;
        const storyIdx = Math.max(0, Math.min(s.stage - 2, 9));
        let story;
        if (dynamicStoriesRef.current && dynamicStoriesRef.current[storyIdx]) {
          const ds = dynamicStoriesRef.current[storyIdx];
          story = { title: ds.title || `REST STOP`, desc: ds.desc || 'A moment of peace...', scene: ds.scene || 'Resting.', colors: (ds.colors && ds.colors.length === 3) ? ds.colors : ['#1a0a3d','#0d0520','#060310'] };
        } else if (isNeonDrift && neonDriftStories[storyIdx]) {
          story = neonDriftStories[storyIdx];
        } else {
          story = { title: `SAFE ZONE`, desc: 'Found a safe area...', scene: 'Resting before the next wave.', colors: ['#1a0a3d','#0d0520','#060310'] };
        }
        const t = s.storyTimer;
        const duration = 140;
        // Background
        const grad = ctx.createLinearGradient(0, 0, 0, h);
        grad.addColorStop(0, story.colors[0]); grad.addColorStop(0.5, story.colors[1]); grad.addColorStop(1, story.colors[2]);
        ctx.fillStyle = grad; ctx.fillRect(0, 0, w, h);

        // Title
        if (t > 15) {
          ctx.globalAlpha = Math.min(1, (t - 15) / 15);
          ctx.fillStyle = '#FFD700';
          ctx.font = `bold ${Math.min(w * 0.06, 38)}px Rajdhani, sans-serif`;
          ctx.textAlign = 'center';
          ctx.fillText(story.title, w / 2, h * 0.2);
          ctx.fillStyle = '#ddd';
          ctx.font = `${Math.min(w * 0.035, 22)}px Rajdhani, sans-serif`;
          ctx.fillText(story.desc, w / 2, h * 0.3);
          const visChars = Math.min(story.scene.length, Math.floor((t - 25) * 1.5));
          if (visChars > 0) {
            ctx.fillStyle = 'rgba(255,255,255,0.7)';
            ctx.font = `italic ${Math.min(w * 0.03, 18)}px Rajdhani, sans-serif`;
            ctx.fillText(story.scene.substring(0, visChars), w / 2, h * 0.37);
          }
          // Story image or fallback
          if (t > 35) {
            const sa = Math.min(1, (t - 35) / 20);
            const sImg = storyImgRef.current[storyIdx];
            if (sImg) {
              ctx.globalAlpha = sa;
              const imgW = Math.min(w * 0.6, 400), imgH = imgW * 0.65;
              const imgX = w / 2 - imgW / 2, imgY = h * 0.4;
              ctx.drawImage(sImg, imgX, imgY, imgW, imgH);
              ctx.strokeStyle = `rgba(255,215,0,0.4)`; ctx.lineWidth = 2;
              ctx.strokeRect(imgX, imgY, imgW, imgH);
            } else {
              ctx.globalAlpha = sa * 0.5;
              ctx.fillStyle = story.colors[0]; ctx.fillRect(w * 0.25, h * 0.42, w * 0.5, h * 0.2);
              ctx.strokeStyle = 'rgba(255,255,255,0.15)'; ctx.strokeRect(w * 0.25, h * 0.42, w * 0.5, h * 0.2);
              if (images.ch) { ctx.globalAlpha = sa * 0.8; const ms = Math.min(w * 0.08, 55); ctx.drawImage(images.ch, w / 2 - ms / 2, h * 0.62 - ms, ms, ms); }
            }
          }
          ctx.globalAlpha = 1;
        }
        if (t > 100) {
          s.storyWaitingForInput = true;
          const fl = Math.sin(t * 0.06) * 0.3 + 0.7;
          ctx.globalAlpha = fl; ctx.fillStyle = '#00F0FF';
          ctx.font = `bold ${Math.min(w * 0.035, 20)}px Rajdhani, sans-serif`; ctx.textAlign = 'center';
          ctx.fillText('PRESS START TO CONTINUE', w / 2, h * 0.88);
          ctx.globalAlpha = fl * 0.15; const btnW = Math.min(w * 0.5, 300);
          ctx.fillStyle = '#00F0FF'; ctx.beginPath(); ctx.roundRect(w / 2 - btnW / 2, h * 0.85, btnW, 28, 14); ctx.fill();
        }
        // Story only advances when user presses start
        ctx.fillStyle = 'rgba(0,0,0,0.5)'; ctx.fillRect(0, 0, w, 32);
        ctx.fillStyle = '#FFD700'; ctx.font = 'bold 13px Rajdhani, sans-serif'; ctx.textAlign = 'center';
        ctx.fillText(`STAGE ${s.stage - 1} → STAGE ${s.stage}`, w / 2, 20);
        ctx.globalAlpha = 1;
        animationRef.current = requestAnimationFrame(loop);
        return;
      }

      // Spawn enemies
      s.enemyTimer++;
      if (s.enemyTimer >= 50) {
        s.enemyTimer = 0;
        s.enemies.push({
          x: 0.2 + Math.random() * 0.6,
          y: 0.25 + Math.random() * 0.35,
          z: 1.0, speed: 0.005 + Math.random() * 0.006,
          frame: 0, alive: true,
        });
      }

      // Update enemies
      s.enemies.forEach(e => {
        e.z -= e.speed; e.frame++;
        e.x += Math.sin(e.frame * 0.03) * 0.003;
      });
      s.enemies = s.enemies.filter(e => e.z > -0.2 && e.alive);

      // Update bullets
      s.bullets.forEach(b => { b.z += b.speed; });
      s.bullets = s.bullets.filter(b => b.z < 1.2);

      // Collision: bullets vs enemies → ENEMY EXPLODES
      for (let b of s.bullets) {
        for (let e of s.enemies) {
          if (!e.alive) continue;
          if (Math.abs(b.z - e.z) < 0.1 && Math.abs(b.x - e.x) < 0.12 && Math.abs(b.y - e.y) < 0.12) {
            e.alive = false;
            b.z = 2;
            s.score += 100;
            spawnExplosion(e.x, e.y, e.z, 20, true); // BIG explosion
          }
        }
      }

      // Collision: enemies vs player → BOTH EXPLODE
      if (s.invincible <= 0) {
        for (let e of s.enemies) {
          if (!e.alive || e.z > 0.15) continue;
          if (Math.abs(e.x - s.playerX) < 0.12 && Math.abs(e.y - s.playerY) < 0.12) {
            s.lives--;
            s.invincible = 60;
            e.alive = false;
            spawnExplosion(e.x, e.y, e.z, 18, true); // Enemy explodes
            spawnExplosion(s.playerX, s.playerY, 0, 12, false); // Player hit sparks
            if (s.lives <= 0) {
              // Player death — BIG explosion
              spawnExplosion(s.playerX, s.playerY, 0, 40, true);
              s.deathExplosion = { life: 50 };
              s.gameOver = true;
            }
          }
        }
      }

      // Update explosion particles
      s.explosions.forEach(ex => {
        ex.x += ex.vx; ex.y += ex.vy; ex.life--;
      });
      s.explosions = s.explosions.filter(ex => ex.life > 0);

      // ──── DRAW ────

      // 1) FULL-SCREEN environment with forward zoom
      if (images.bg) {
        const p1 = s.zoomPhase;
        const p2 = (s.zoomPhase + 0.5) % 1.0;
        const z1 = 1.0 + p1 * ZOOM_RANGE;
        const z2 = 1.0 + p2 * ZOOM_RANGE;
        const fade = Math.abs(Math.sin(p1 * Math.PI));
        ctx.globalAlpha = fade;
        drawEnvZoomed(images.bg, w, h, z1);
        ctx.globalAlpha = 1.0 - fade;
        drawEnvZoomed(images.bg, w, h, z2);
        ctx.globalAlpha = 1.0;
      } else {
        const grad = ctx.createLinearGradient(0, 0, 0, h);
        grad.addColorStop(0, '#050520');
        grad.addColorStop(0.4, '#0a1040');
        grad.addColorStop(0.7, '#1a3a5c');
        grad.addColorStop(1, '#0a1a2e');
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, w, h);
      }

      // 2) Explosion particles
      s.explosions.forEach(ex => {
        const scale = Math.max(0.15, 1 - (ex.z || 0));
        const sx = vanishX + (ex.x - 0.5) * w * scale;
        const sy = vanishY + (ex.y - 0.3) * (h - vanishY) * scale;
        ctx.globalAlpha = Math.max(0, ex.life / ex.maxLife);
        ctx.fillStyle = ex.color;
        ctx.beginPath(); ctx.arc(sx, sy, ex.size * scale, 0, Math.PI * 2); ctx.fill();
        // Inner glow
        ctx.fillStyle = '#fff';
        ctx.beginPath(); ctx.arc(sx, sy, ex.size * scale * 0.4, 0, Math.PI * 2); ctx.fill();
      });
      ctx.globalAlpha = 1;

      // 3) Draw enemies — BIGGER
      s.enemies.forEach(e => {
        if (!e.alive) return;
        const scale = Math.max(0.1, 1 - e.z);
        const screenX = vanishX + (e.x - 0.5) * w * scale;
        const screenY = vanishY + (e.y - 0.3) * (h - vanishY) * scale;
        const eSize = 120 * scale; // BIGGER enemies
        if (images.en) {
          ctx.save();
          ctx.translate(screenX, screenY);
          const wobble = Math.sin(e.frame * 0.08) * 0.1 * scale;
          ctx.rotate(wobble);
          ctx.drawImage(images.en, -eSize / 2, -eSize / 2, eSize, eSize);
          ctx.restore();
        } else {
          ctx.fillStyle = '#f44'; ctx.shadowColor = '#f00'; ctx.shadowBlur = 10 * scale;
          ctx.fillRect(screenX - eSize / 2, screenY - eSize / 2, eSize, eSize);
          ctx.shadowBlur = 0;
        }
        // Targeting reticle
        if (e.z < 0.5) {
          ctx.strokeStyle = `rgba(255,0,0,${(0.5 - e.z) * 2})`;
          ctx.lineWidth = 1.5;
          ctx.strokeRect(screenX - eSize * 0.65, screenY - eSize * 0.65, eSize * 1.3, eSize * 1.3);
          // Corner brackets
          const bsz = eSize * 0.2;
          ctx.beginPath();
          ctx.moveTo(screenX - eSize*0.65, screenY - eSize*0.65 + bsz);
          ctx.lineTo(screenX - eSize*0.65, screenY - eSize*0.65);
          ctx.lineTo(screenX - eSize*0.65 + bsz, screenY - eSize*0.65);
          ctx.stroke();
        }
      });

      // 4) Draw bullets (tracer lines)
      s.bullets.forEach(b => {
        const scale = Math.max(0.1, 1 - b.z);
        const sx = vanishX + (b.x - 0.5) * w * scale;
        const sy = vanishY + (b.y - 0.3) * (h - vanishY) * scale;
        const prevScale = Math.max(0.1, 1 - (b.z - 0.02));
        const px = vanishX + (b.x - 0.5) * w * prevScale;
        const py = vanishY + (b.y - 0.3) * (h - vanishY) * prevScale;
        ctx.strokeStyle = `rgba(0,255,255,${Math.max(0, 1 - b.z)})`;
        ctx.lineWidth = 3 * scale;
        ctx.beginPath(); ctx.moveTo(px, py); ctx.lineTo(sx, sy); ctx.stroke();
        ctx.strokeStyle = `rgba(0,255,255,${Math.max(0, 0.3 - b.z * 0.3)})`;
        ctx.lineWidth = 8 * scale;
        ctx.beginPath(); ctx.moveTo(px, py); ctx.lineTo(sx, sy); ctx.stroke();
      });

      // 5) Draw player ship — BIGGER
      const px = s.playerX * w;
      const py = s.playerY * h;
      const shipW = 140; // BIGGER ship
      const shipH = 120;
      const blink = s.invincible > 0 && Math.floor(s.frame / 4) % 2 === 0;
      if (!blink) {
        ctx.save();
        ctx.translate(px, py);
        ctx.rotate(s.roll);
        // Engine glow
        ctx.fillStyle = 'rgba(0,150,255,0.3)';
        ctx.beginPath();
        ctx.ellipse(0, shipH * 0.3, 16, 25 + Math.sin(s.frame * 0.3) * 5, 0, 0, Math.PI * 2);
        ctx.fill();
        if (images.ch) {
          const pulse = 1 + Math.sin(s.frame * 0.08) * 0.02;
          ctx.scale(pulse, pulse);
          ctx.drawImage(images.ch, -shipW / 2, -shipH / 2, shipW, shipH);
        } else {
          ctx.fillStyle = '#0af'; ctx.shadowColor = '#0af'; ctx.shadowBlur = 15;
          ctx.beginPath();
          ctx.moveTo(0, -shipH / 2);
          ctx.lineTo(-shipW / 2, shipH / 2);
          ctx.lineTo(shipW / 2, shipH / 2);
          ctx.closePath(); ctx.fill();
          ctx.shadowBlur = 0;
        }
        ctx.restore();
      }

      // 6) HUD
      ctx.fillStyle = 'rgba(0,0,0,0.4)';
      ctx.fillRect(0, h - 40, w, 40);
      ctx.fillStyle = '#0af'; ctx.font = 'bold 14px Rajdhani, sans-serif'; ctx.textAlign = 'left';
      ctx.fillText(`ALT: ${Math.floor((1 - s.playerY) * 10000)}ft`, 10, h - 15);
      ctx.fillText(`SPD: ${Math.floor(s.speed * 200)}kts`, 150, h - 15);
      ctx.textAlign = 'right';
      ctx.fillText(`STAGE ${s.stage}`, w - 10, h - 15);
      for (let i = 0; i < s.lives; i++) {
        ctx.fillStyle = '#f00';
        ctx.fillText('\u2665', w - 80 - i * 18, h - 15);
      }

      // 7) Crosshair
      ctx.strokeStyle = 'rgba(0,255,255,0.4)';
      ctx.lineWidth = 1;
      ctx.beginPath(); ctx.arc(px, py - 80, 18, 0, Math.PI * 2); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(px, py - 103); ctx.lineTo(px, py - 93); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(px, py - 67); ctx.lineTo(px, py - 57); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(px - 23, py - 80); ctx.lineTo(px - 13, py - 80); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(px + 13, py - 80); ctx.lineTo(px + 23, py - 80); ctx.stroke();

      animationRef.current = requestAnimationFrame(loop);
    };

    animationRef.current = requestAnimationFrame(loop);
    return () => {
      running = false;
      cancelAnimationFrame(animationRef.current);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      delete window.gameActions;
    };
  }, [isPlaying]);

  return (
    <canvas
      ref={canvasRef}
      data-testid="flight-canvas"
      className="absolute inset-0 w-full h-full"
      style={{ touchAction: 'none' }}
      onClick={() => { if (window.gameActions?.advanceStory) window.gameActions.advanceStory(); }}
    />
  );
};

export default FlightMode;
